/**
 * Live-exam interventions, scoped to one exam.
 *
 * Everything here changes a sitting that is already under way, or a record a
 * student has already been graded on, so each action states its blast radius
 * before it is confirmed and each one is written to the intervention log the
 * card renders underneath. An admin who cannot see what was done to an exam,
 * by whom and why, ends up guessing at a disputed result months later.
 *
 * The two actions differ in exactly that blast radius, and the copy leans hard
 * on the difference: extending moves the deadline for *everyone* on the paper,
 * while a retake opens one new attempt for *one* student and leaves every
 * earlier attempt untouched.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, RotateCcw } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  Input,
  Select,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  useToast,
} from "@/components/ui";
import { ADMIN_ACTION_LABEL } from "@exam/shared";
import type { AdminActionEntry } from "@exam/shared";
import {
  extendExam,
  fetchExamActions,
  fetchUsers,
  grantRetake,
} from "../data/client";
import type { ExamRow } from "../data/types";
import { formatDateTime } from "../lib/format";
import { useAsync } from "../lib/useAsync";

export function ExamActionsCard({
  exam,
  onChanged,
}: {
  exam: ExamRow;
  /** Re-read the exam — an extension changes `duration_min` on the server. */
  onChanged: () => void;
}) {
  const { toast } = useToast();
  const [extendOpen, setExtendOpen] = useState(false);
  const [retakeOpen, setRetakeOpen] = useState(false);

  const log = useAsync(() => fetchExamActions(exam.id), [exam.id]);

  const afterAction = () => {
    onChanged();
    log.reload();
  };

  return (
    <Card>
      <CardHeader
        title="During the exam"
        description="Interventions on this paper. Every one of them is logged with your name and note."
        actions={
          <div className="flex gap-2">
            <Button
              size="sm"
              icon={<Clock />}
              onClick={() => setExtendOpen(true)}
            >
              Extend exam
            </Button>
            <Button
              size="sm"
              icon={<RotateCcw />}
              onClick={() => setRetakeOpen(true)}
            >
              Grant retake
            </Button>
          </div>
        }
      />

      <InterventionLog state={log} />

      <ExtendExamDialog
        open={extendOpen}
        exam={exam}
        onClose={() => setExtendOpen(false)}
        onDone={(minutes) => {
          setExtendOpen(false);
          toast({
            tone: "success",
            title: `Exam ${minutes > 0 ? "extended" : "shortened"} by ${Math.abs(minutes)} minutes.`,
            description: "Everyone sitting this paper has the new deadline.",
          });
          afterAction();
        }}
        onError={(message) =>
          toast({
            tone: "error",
            title: "Could not extend the exam.",
            description: message,
          })
        }
      />

      <GrantRetakeDialog
        open={retakeOpen}
        exam={exam}
        onClose={() => setRetakeOpen(false)}
        onDone={(studentName, attemptNo) => {
          setRetakeOpen(false);
          toast({
            tone: "success",
            title: `Retake opened for ${studentName}.`,
            description: `This is attempt ${attemptNo}. The earlier attempt is unchanged.`,
          });
          afterAction();
        }}
        onError={(message) =>
          toast({
            tone: "error",
            title: "Could not grant the retake.",
            description: message,
          })
        }
      />
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Intervention log                                                    */
/* ------------------------------------------------------------------ */

/**
 * Nothing at all until something has been done to this exam. An empty table
 * with three headers and a "no rows" line is a worse answer than silence: it
 * takes up the space of real content to say that there is none.
 */
function InterventionLog({
  state,
}: {
  state: { data: AdminActionEntry[] | null; error: unknown; loading: boolean };
}) {
  if (state.error !== null && state.error !== undefined) {
    return (
      <CardBody>
        <p className="text-2xs text-ink-faint">
          The intervention log could not be read.
        </p>
      </CardBody>
    );
  }
  if (state.data === null || state.data.length === 0) return null;

  return (
    <Table>
      <THead>
        <tr>
          <TH>Action</TH>
          <TH>By</TH>
          <TH>Note</TH>
          <TH>When</TH>
        </tr>
      </THead>
      <TBody>
        {state.data.map((entry) => (
          <TR key={entry.id}>
            <TD>
              <Badge tone="review">{ADMIN_ACTION_LABEL[entry.action]}</Badge>
            </TD>
            <TD className="text-ink-muted">{entry.actor_name}</TD>
            <TD className="max-w-sm text-ink-muted">
              <span className="block truncate">{entry.note ?? "—"}</span>
            </TD>
            <TD className="tabular text-ink-muted">
              {formatDateTime(entry.ts_ms)}
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}

/* ------------------------------------------------------------------ */
/* Extend                                                              */
/* ------------------------------------------------------------------ */

function ExtendExamDialog({
  open,
  exam,
  onClose,
  onDone,
  onError,
}: {
  open: boolean;
  exam: ExamRow;
  onClose: () => void;
  onDone: (minutes: number) => void;
  onError: (message: string) => void;
}) {
  const [minutes, setMinutes] = useState("10");
  const [extendWindow, setExtendWindow] = useState(true);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  // The dialog stays mounted, so without this it reopens holding the last
  // extension's minutes and, worse, the last note — which is filed verbatim
  // as the audit reason for a completely different intervention.
  useEffect(() => {
    if (!open) return;
    setMinutes("10");
    setExtendWindow(true);
    setNote("");
  }, [open]);

  const value = Number(minutes);
  // The API bounds this at ±600 and refuses zero; catching it here keeps a
  // typo from turning into a round trip that only says "invalid".
  //
  // The shortening case is checked too: this moves the deadline for everyone
  // currently sitting the paper, so a value that drives the duration to zero
  // would end every one of their attempts at once.
  const resulting = exam.duration_min + (Number.isFinite(value) ? value : 0);
  const invalid =
    !Number.isInteger(value) ||
    value === 0 ||
    Math.abs(value) > 600 ||
    resulting <= 0;
  const hasWindow = exam.available_until !== null;

  const submit = async () => {
    setBusy(true);
    try {
      await extendExam(exam.id, value, {
        extendWindow: hasWindow && extendWindow,
        note: note.trim() === "" ? null : note.trim(),
      });
      onDone(value);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Request failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      busy={busy}
      open={open}
      onClose={onClose}
      title="Extend this exam"
      description="Changes the paper's duration, not one person's clock."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={busy || invalid}
            onClick={() => void submit()}
          >
            {value < 0 ? "Shorten" : "Extend"} the exam
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="rounded-md border border-review/25 bg-review-soft px-3 py-2 text-xs text-review">
          This affects <strong>everyone</strong> sitting this exam, including
          attempts that are running right now — the deadline is derived from the
          duration, so it moves the moment this is saved.
        </p>

        <Input
          label="Minutes"
          type="number"
          min={-600}
          max={600}
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
          error={invalid ? "Whole minutes, between −600 and 600, not zero." : null}
          hint={`Now ${exam.duration_min} minutes${
            invalid ? "" : ` → ${exam.duration_min + value} minutes`
          }.`}
        />

        <label className="flex items-start gap-2.5 rounded-md border border-border bg-surface-sunken px-3 py-2.5">
          <input
            type="checkbox"
            checked={hasWindow && extendWindow}
            disabled={!hasWindow}
            onChange={(e) => setExtendWindow(e.target.checked)}
            className="mt-0.5 size-4 rounded-xs border-border-strong text-accent focus:ring-accent disabled:opacity-50"
          />
          <span className="min-w-0">
            <span className="block text-xs font-medium text-ink">
              Push &ldquo;must start by&rdquo; out by the same amount
            </span>
            <span className="block text-2xs text-ink-muted">
              {hasWindow
                ? `Currently ${formatDateTime(exam.available_until)}. Leave this on when the extension is because the sitting started late.`
                : "This exam has no start-by deadline, so there is nothing to move."}
            </span>
          </span>
        </label>

        <Input
          label="Note"
          value={note}
          placeholder="Power cut in the lab — 10 minutes lost."
          onChange={(e) => setNote(e.target.value)}
          hint="Optional, but it is what the log will show months from now."
        />
      </div>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Retake                                                              */
/* ------------------------------------------------------------------ */

function GrantRetakeDialog({
  open,
  exam,
  onClose,
  onDone,
  onError,
}: {
  open: boolean;
  exam: ExamRow;
  onClose: () => void;
  onDone: (studentName: string, attemptNo: number) => void;
  onError: (message: string) => void;
}) {
  const [studentId, setStudentId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  // Reopening with the previous student still selected and the previous note
  // still in the box is how you grant a second retake to someone who did not
  // ask for one, and file somebody else's reason against it.
  useEffect(() => {
    if (!open) return;
    setStudentId("");
    setNote("");
  }, [open]);

  const students = useAsync(
    () => fetchUsers({ role: "student", active: true }),
    [],
    { enabled: open },
  );

  const chosen = (students.data?.users ?? []).find((u) => u.id === studentId);

  const submit = async () => {
    if (!chosen) return;
    setBusy(true);
    try {
      const attempt = await grantRetake(
        exam.id,
        chosen.id,
        note.trim() === "" ? null : note.trim(),
      );
      onDone(chosen.name, attempt.attempt_no);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Request failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      busy={busy}
      open={open}
      onClose={onClose}
      title="Grant a retake"
      description={exam.title}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={busy || !chosen}
            onClick={() => void submit()}
          >
            Open a new attempt
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="space-y-1.5 rounded-md border border-border bg-surface-sunken px-3 py-2.5 text-xs text-ink-muted">
          <p className="font-medium text-ink">
            A retake is a second sitting, not a second chance at the first one.
          </p>
          <p>
            The student gets a <strong>new attempt on the same paper</strong>,
            numbered one higher, starting empty with a full clock.
          </p>
          <p>
            The attempt they have already made is left exactly as it is — its
            answers, its recording, its violations and its grade all survive,
            and it stays open for review. Nothing is overwritten and nothing is
            deleted.
          </p>
          <p>
            To let a student carry on with the attempt they already started,
            reopen or extend that attempt from the live monitor instead. This is
            not that.
          </p>
        </div>

        <Select
          label="Student"
          value={studentId}
          onChange={(e) => setStudentId(e.target.value)}
        >
          <option value="">
            {students.loading ? "Loading students…" : "Choose a student"}
          </option>
          {(students.data?.users ?? []).map((user) => (
            <option key={user.id} value={user.id}>
              {user.name} — {user.email}
            </option>
          ))}
        </Select>
        {students.error !== null && !students.loading ? (
          <p className="text-2xs text-violation">
            The student list could not be read, so nobody can be picked here.
          </p>
        ) : null}

        <Input
          label="Note"
          value={note}
          placeholder="Laptop died 20 minutes in; approved by the coordinator."
          onChange={(e) => setNote(e.target.value)}
          hint="Optional. This is the record of why a second sitting was allowed."
        />
      </div>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Live link                                                           */
/* ------------------------------------------------------------------ */

/**
 * Shown wherever an exam has attempts in progress. During a sitting the live
 * monitor is the screen that matters, and having to find it in the nav while a
 * clock is running is exactly the wrong moment to go hunting.
 */
export function LiveMonitorLink({
  examId,
  running,
  className,
}: {
  examId: string;
  running: number;
  className?: string;
}) {
  if (running === 0) return null;
  return (
    <Link
      to={`/admin/live?exam_id=${examId}`}
      className={className}
      title="Open the live monitor for this exam"
    >
      <Badge tone="review">
        <span className="tabular">{running}</span> running · watch
      </Badge>
    </Link>
  );
}
